import type { CreateResumeDto, ResumeDto } from "@stylingcv/dto";
import { useMutation } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";

import { RESUMES_KEY } from "@/client/constants/query-keys";
import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";
import { trackResumeEvent } from "@/client/utils/gtm";

export const createResume = async (data: CreateResumeDto) => {
  // Runtime check: set El Messiri as default font for Arabic locale
  const _data: any = data;
  const locale = _data?.data?.metadata?.locale?.toLowerCase?.() || "";
  const isArabic = locale === "ar" || locale === "arabic" || locale.startsWith("ar-");
  if (isArabic) {
    const currentFont = _data?.data?.metadata?.typography?.font?.family;
    const arabicFonts = [
      "Tajawal",
      "Cairo",
      "Amiri",
      "Changa",
      "El Messiri",
      "Harmattan",
      "Lateef",
      "Lalezar",
      "Markazi Text",
      "Reem Kufi",
      "Scheherazade New",
      "Noto Naskh Arabic",
      "Noto Sans Arabic",
      "Almarai",
      "Aref Ruqaa",
      "Baloo Bhaijaan 2",
      "Mada",
      "Mirza",
      "Rakkas",
      "Vazirmatn",
    ];
    if (!currentFont || !arabicFonts.includes(currentFont)) {
      _data.data = _data.data || {};
      _data.data.metadata = _data.data.metadata || {};
      _data.data.metadata.typography = _data.data.metadata.typography || {};
      _data.data.metadata.typography.font = {
        family: "Tajawal",
        subset: "arabic",
        variants: ["regular"],
        size: _data.data.metadata.typography.font?.size || 14,
      };
    }
  }
  const response = await axios.post<ResumeDto, AxiosResponse<ResumeDto>, CreateResumeDto>(
    "/resume",
    _data,
  );

  return response.data;
};

export const useCreateResume = () => {
  const {
    error,
    isPending: loading,
    mutateAsync: createResumeFn,
  } = useMutation({
    mutationFn: createResume,
    onSuccess: (data) => {
      queryClient.setQueryData<ResumeDto>(["resume", { id: data.id }], data);

      queryClient.setQueryData<ResumeDto[]>(["resumes"], (cache) => {
        if (!cache) return [data];
        return [...cache, data];
      });

      // Also invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: RESUMES_KEY });

      // Track resume creation
      trackResumeEvent.created();
    },
  });

  return { createResume: createResumeFn, loading, error };
};
